import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, FolderPlus } from '@digital-twins/design-system/icons'
import { SocialUniverseItem } from '@digital-twins/shared-types'
import { PageLayout } from '@/components/layout/PageLayout'
import { AddToCollectionMenu } from '@/components/library/AddToCollectionMenu'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { useAssistantPlatformEnabled } from '@/hooks/useAssistantPlatformEnabled'
import { useItemCollections } from '@/pages/KnowledgeHubPage/collections/useItemCollections'
import { KnowledgePageShell } from '@/layouts/KnowledgePageShell/KnowledgePageShell'
import { useAuth } from '@/lib/config/auth-provider'
import Link from '@/routing/Link'
import { ROUTES } from '@/routes/routes'
import { SocialTopicView } from '@/pages/KnowledgeHubPage/social/SocialTopicView'
import { SocialUniverseDialog } from '@/pages/KnowledgeHubPage/social/SocialUniverseDialog'
import { useSocialUniverses } from '@/pages/KnowledgeHubPage/social/useSocialUniverses'

/**
 * One social listening Topic as a full page (`/knowledge/topics/:token`).
 * The route gates on the assistant platform flag; this page also needs
 * social listening on for the org, and otherwise goes to the Library's
 * Social listening tab. Leaving the Topic (Back, last channel removed) lands
 * there too. The page owns the Edit topic dialog. The Topic's filters, sort
 * and measure live in the page URL, so a link opens the same view.
 */
interface TopicCollectionsExtrasProps {
  token: string
}

/**
 * The Topic header's collection entry points: the add menu and an "In {n}
 * collections" popover (hidden at zero). Only mounted on this page route, so
 * the back office drawer never calls `/library`.
 */
function TopicCollectionsExtras(props: TopicCollectionsExtrasProps) {
  const { token } = props
  const { t } = useTranslation('app')
  const { collections } = useItemCollections({ kind: 'topic', token })

  return (
    <>
      {collections.length > 0 ? (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-[34px] rounded-lg px-3 font-medium"
              data-testid="knowledge-topic-in-collections"
            >
              {t('library.collections.topics.inCollections', { count: collections.length })}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64" data-testid="knowledge-topic-collections-list">
            <ul className="flex flex-col">
              {collections.map((collection) => (
                <li key={collection.token}>
                  <Link
                    href={ROUTES.knowledgeCollection(collection.token)}
                    className="block rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-accent"
                    data-testid={`knowledge-topic-collection-link-${collection.token}`}
                  >
                    {collection.name}
                  </Link>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      ) : null}
      <AddToCollectionMenu
        items={[{ kind: 'topic', token }]}
        testId="knowledge-topic-add-to-collection"
        trigger={
          // Icon-only below the header's container breakpoint, like Activity, Edit and Update now.
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-[34px] gap-2 rounded-lg px-3 font-medium [&>span]:hidden @[760px]/topic:[&>span]:inline"
            aria-label={t('library.collections.addToCollection')}
            title={t('library.collections.addToCollection')}
            data-testid="knowledge-topic-add-to-collection-trigger"
          >
            <FolderPlus className="h-4 w-4" aria-hidden="true" />
            <span>{t('library.collections.addToCollection')}</span>
          </Button>
        }
      />
    </>
  )
}

/** The origin's name from the router state, when a link set one. */
function readBackLabel(state: unknown): string | null {
  if (state && typeof state === 'object' && 'backLabel' in state) {
    const value = (state as { backLabel?: unknown }).backLabel
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
  }
  return null
}

/** The origin's in-app path from the router state (a path, never an external URL). */
function readBackHref(state: unknown): string | null {
  if (state && typeof state === 'object' && 'backHref' in state) {
    const value = (state as { backHref?: unknown }).backHref
    if (typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')) {
      return value
    }
  }
  return null
}

export default function KnowledgeTopicPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation('app')
  const { isLoading: isAuthLoading } = useAuth()
  const assistantPlatformEnabled = useAssistantPlatformEnabled()
  const canLoad = !isAuthLoading && assistantPlatformEnabled
  const socialUniverses = useSocialUniverses(canLoad)

  const [isEditOpen, setIsEditOpen] = useState(false)
  const [editingUniverse, setEditingUniverse] = useState<SocialUniverseItem | null>(null)

  // `location.key` is 'default' on the first entry of the session (a direct
  // load or a reload), so there is nothing in-app to return to.
  const hasInAppHistory = location.key !== 'default'
  // A collection's Topic link names itself (`{ backLabel }` in the router state).
  const originLabel = readBackLabel(location.state)
  // The href follows the same origin as the label, so hover, cmd-click and
  // open in a new tab land where Back returns.
  const originHref = hasInAppHistory ? readBackHref(location.state) : null

  const leaveTopic = () => {
    if (hasInAppHistory) {
      navigate(-1)
      return
    }
    navigate(ROUTES.knowledgeSocial)
  }

  const onBackClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    leaveTopic()
  }

  const onEdit = (universe: SocialUniverseItem) => {
    setEditingUniverse(universe)
    setIsEditOpen(true)
  }

  const backLink = (
    <Link
      href={originHref ?? ROUTES.knowledgeSocial}
      onClick={onBackClick}
      className="relative z-10 inline-flex items-center gap-1.5 rounded-md text-sm text-muted-foreground transition-colors hover:text-foreground"
      data-testid="knowledge-topic-back"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      {/* With in-app history the link returns wherever the Topic was opened
          from: named when the origin said who it is ("Back to RTD"), else
          "Back"; opened directly it goes to Social listening and says so. */}
      {hasInAppHistory
        ? originLabel
          ? t('socialListening.topicPage.backTo', { name: originLabel })
          : t('socialListening.topicPage.backGeneric')
        : t('socialListening.topicPage.back')}
    </Link>
  )

  if (isAuthLoading || socialUniverses.isLoading) {
    return (
      <PageLayout className="max-w-none gap-5 p-6 md:p-8" data-testid="knowledge-topic-loading">
        <div>{backLink}</div>
        <Skeleton className="h-8 w-1/3 rounded-lg" />
        <Skeleton className="h-40 rounded-xl" />
      </PageLayout>
    )
  }

  if (!token || !socialUniverses.isEnabled) {
    return <Navigate to={ROUTES.knowledgeSocial} replace />
  }

  const fallback = socialUniverses.universes.find((universe) => universe.token === token) ?? null

  return (
    <KnowledgePageShell
      testId="knowledge-topic"
      overlays={
        <SocialUniverseDialog
          open={isEditOpen}
          universe={editingUniverse}
          onOpenChange={setIsEditOpen}
          onSaved={socialUniverses.replaceUniverse}
        />
      }
    >
      <SocialTopicView
        key={token}
        universeToken={token}
        fallback={fallback}
        canManage={socialUniverses.canManage}
        syncSchedule={socialUniverses.schedule}
        layout="page"
        filtersUrl={{ searchParams, setSearchParams }}
        headerLeading={backLink}
        headerExtras={<TopicCollectionsExtras token={token} />}
        onClose={leaveTopic}
        onEdit={onEdit}
        onUniverseChanged={socialUniverses.replaceUniverse}
        onUniverseDeleted={socialUniverses.removeUniverse}
        onRefresh={() => void socialUniverses.refresh()}
      />
    </KnowledgePageShell>
  )
}
